import { TourPlayback } from "@common/schemas";
import { getUrl, isUrlEqualsToCurrentUrl } from "../window";
import { waitForOnTrackConditions } from "./onTrackConditionCheck";
import * as models from "@common/models/v2";
import { TourState } from "./persistent-storage/tourState";
import { SuspendReason } from "./tourPlayback/types";
import { findLastCheckpoint } from "./checkpoint";

export type TourStartStrategyType = "startOver" | "resume" | "bringBack" | "startSuspended";

export type BaseTourStartStrategy = {
  type: TourStartStrategyType;
  description?: string;
  message?: string;
};

export type StartOverStrategy = BaseTourStartStrategy & {
  type: "startOver";
};

export type ResumeStrategy = BaseTourStartStrategy & {
  type: "resume";
  stepId: string;
};

export type BringBackStrategy = BaseTourStartStrategy & {
  type: "bringBack";
  url: string;
  stepId: string;
};

export type StartSuspendedStrategy = BaseTourStartStrategy & {
  type: "startSuspended";
  reason: SuspendReason;
};

export type TourStartStrategy = StartOverStrategy | ResumeStrategy | BringBackStrategy | StartSuspendedStrategy;

export class TourStartManager {
  initialTourStateSnapshot?: TourState;

  async decideStrategy(tabId: string, tourPlayback: TourPlayback, tourState?: TourState): Promise<TourStartStrategy> {
    this.saveInitialTourStateSnapshot(tourState);

    const isActiveTourExists = this.isActiveTourExists(tourState);
    if (!isActiveTourExists) {
      return { type: "startOver", message: "No active tour exists" };
    }

    const isActiveTourSuspended = this.isActiveTourSuspended({ withReason: ["userAction"] }, tourState);
    if (isActiveTourSuspended) {
      return { type: "startSuspended", message: "Active tour is suspended", reason: "userAction" };
    }

    const isActiveTourPlayingOnOtherTab = this.isActiveTourPlayingOnOtherTab(tabId, tourState);
    if (isActiveTourPlayingOnOtherTab) {
      return {
        type: "startSuspended",
        message: "Active tour is playing on other tab",
        reason: "tourPlayingOnOtherTab",
      };
    }

    const activeStepId = tourState?.activeStepId;
    const lastCheckpoint = this.getLastCheckpoint(tourState);
    const lastCompletedStepId = tourState?.lastCompletedStepId;

    if (!lastCheckpoint || !activeStepId) {
      return { type: "startOver", message: "No last checkpoint or active step" };
    }

    const isUrlChanged = this.isLastPageUrlChanged(tourState);
    const isActiveStepEqualsLastCheckpointStep = lastCheckpoint.stepId === activeStepId;
    const shouldContinueFromNextStep = isActiveStepEqualsLastCheckpointStep || isUrlChanged;

    if (shouldContinueFromNextStep) {
      const nextStepId = this.getNextStepId(tourPlayback, lastCompletedStepId);
      if (!nextStepId) {
        // TODO: handle different
        return {
          type: "startOver",
          message: "No next step after last completed step",
        };
      }

      const isNextStepOnTrack = await this.isStepOnTrack(nextStepId, tourPlayback);
      if (isNextStepOnTrack) {
        return {
          type: "resume",
          stepId: nextStepId,
          description: "Will continue from step after last completed step",
          message: "Handling full page reloads on page transitions",
        };
      }
    }

    if (isUrlEqualsToCurrentUrl(lastCheckpoint.url)) {
      return {
        type: "resume",
        stepId: lastCheckpoint.stepId,
        description: "Will continue from last checkpoint",
        message: "Last checkpoint is on the same page",
      };
    }

    return {
      type: "bringBack",
      url: lastCheckpoint.url,
      stepId: lastCheckpoint.stepId,
      message: "Last checkpoint is on a different page",
    };
  }

  private isLastPageUrlChanged(tourState: TourState): boolean {
    const lastPageUrl = tourState.lastPageUrl;
    if (!lastPageUrl) {
      return false;
    }
    return !isUrlEqualsToCurrentUrl(lastPageUrl);
  }

  private getNextStepId(tourPlayback: TourPlayback, stepId?: string): string | undefined {
    if (!stepId) {
      return tourPlayback.sections[0].steps[0].id;
    }

    const steps = tourPlayback.sections.flatMap((section) => section.steps);
    const stepIndex = steps.findIndex((step) => step.id === stepId);

    const isStepNotFound = stepIndex === -1;
    const isLastStep = stepIndex === steps.length - 1;
    if (isStepNotFound || isLastStep) {
      return;
    }

    return steps[stepIndex + 1].id;
  }

  private async isStepOnTrack(stepId: string, tourPlayback: TourPlayback): Promise<boolean> {
    const step = tourPlayback.sections.flatMap((section) => section.steps).find((step) => step.id === stepId);

    if (!step) {
      return false;
    }

    const stepModel = new models.Step(step);
    const url = getUrl();
    return await waitForOnTrackConditions(stepModel.attributes.onTrackConditions, url);
  }

  isActiveTourExists(tourState?: TourState): boolean {
    return !!tourState?.activeTourGuideId;
  }

  isActiveTourSuspended({ withReason }: { withReason: SuspendReason[] }, tourState?: TourState): boolean {
    if (!tourState?.activeTourGuideId) {
      return false;
    }

    if (!tourState.isSuspended || !tourState.suspendReason) {
      return false;
    }

    return tourState.isSuspended && withReason.map((r) => r.toString()).includes(tourState.suspendReason);
  }

  private isActiveTourPlayingOnOtherTab(tabId: string, tourState?: TourState) {
    if (!tourState?.activeTourGuideId) {
      return false;
    }

    return tourState.tabId !== tabId;
  }

  private saveInitialTourStateSnapshot(tourState?: TourState) {
    if (tourState) {
      this.initialTourStateSnapshot = tourState;
      return;
    }
  }

  private getLastCheckpoint(tourState?: TourState) {
    if (!tourState) {
      return null;
    }

    const checkpoints = tourState.checkpoints;
    if (!checkpoints) {
      return null;
    }

    return findLastCheckpoint(checkpoints);
  }
}
